import React, { useState, useEffect, useReducer, useRef, useContext, useMemo } from "react";

import { isSameDay } from "date-fns";
import clsx from "clsx";
import { useTimezone } from "../../hooks/useTimezone";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  DoneAll,
  ExpandMore,
  GetApp,
  Reply,
  CheckCircleOutline,
  CheckCircle,
  Notifications,
  SwapHoriz,
  Note,
  Info,
  Person,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import LocationPreview from "../LocationPreview";
import VCardPreview from "../VCardPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import { i18n } from "../../translate/i18n";
import { BORDERS, SHADOWS, CHAT } from "../../theme/redesign";

/*
 * REVISIÓN 2026-09 — HECHO en esta pasada:
 *   #1  socket.off ahora remueve solo los handlers propios (helper `on()` + `listeners`);
 *       eliminados los `socket.off("company-*-contact")` / `socket.off("disconnect")` que
 *       borraban listeners de otros componentes para eventos que este efecto no registra.
 *   #7  ref al contenedor (`messagesListRef`) en vez de document.getElementById("messagesList"),
 *       que colisionaba al montar otro MessagesList (TicketMessagesDialog). El id del div ahora
 *       incluye el ticketId.
 *   #8  scrollToBottom(force): la carga inicial y los mensajes propios bajan siempre; los
 *       mensajes entrantes solo bajan si el usuario ya estaba al final (isNearBottom). El
 *       efecto de "scroll al poblar" ahora dispara una sola vez por hilo (didInitialScrollRef).
 *   #12 renderMessages() memoizado con useMemo: ya no filtra+mapea el hilo en cada render del
 *       componente (menú de opciones, loading, paginación...). Solo se recalcula al cambiar
 *       messagesList / theme / isGroup / readOnlyPreview / showSelectMessageCheckbox / formatters.
 *   - ingesta unificada: un solo `handleIncomingMessage` registrado para `company-*-appMessage`
 *     y para el canal por ticket (antes ~40 líneas duplicadas). `company-*-appMessage-update`
 *     se deja aparte (es de acks/estado, no create). El reducer deduplica por id.
 *   - reducer inmutable: nada de state[i]= / push sobre `state`.
 *
 * PENDIENTE (fuera del alcance de esta pasada):
 *   #12b para O(1) por mensaje nuevo (no re-mapear el hilo entero al llegar uno), hay que
 *        extraer un <MessageItem> con React.memo. Es un refactor grande de ~600 líneas de JSX
 *        entrelazado (renderDailyTimestamps/renderNumberTicket/renderMessageDivider dependen de
 *        vecinos) y necesita verificación en navegador antes de tocarlo.
 *   - el `create` sigue procesándose 2 veces (llega por `company-*-appMessage` y por el canal
 *     del ticket); es idempotente en el reducer, pero podría de-duplicarse con un set de ids.
 */

// Función para formatear el tamaño del archivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'kB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ✅ FUNCIÓN HELPER: Normalizar URLs de media (aplicar misma solución que imágenes)
// Corrige doble /public/ y construye URLs absolutas correctamente
const normalizeMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return "";
  
  const origin = window.location.origin.replace(/\/$/, "");
  let normalizedUrl = mediaUrl.trim();
  
  // ✅ Si ya es URL absoluta (http/https/blob), normalizar doble /public/
  if (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://") || normalizedUrl.startsWith("blob:")) {
    // ✅ CORRECCIÓN CRÍTICA: Eliminar TODAS las ocurrencias de doble /public/
    normalizedUrl = normalizedUrl.replace(/\/public\/\/public\//g, '/public/');
    normalizedUrl = normalizedUrl.replace(/\/public\/public\//g, '/public/');
    return normalizedUrl;
  }
  
  // ✅ Si empieza con /public/, construir URL completa
  if (normalizedUrl.startsWith("/public/")) {
    // ✅ CORRECCIÓN CRÍTICA: Eliminar doble /public/ ANTES de construir URL completa
    normalizedUrl = normalizedUrl.replace(/^\/public\/\/public\//, '/public/');
    normalizedUrl = normalizedUrl.replace(/^\/public\/public\//, '/public/');
    return `${origin}${normalizedUrl}`;
  }
  
  // ✅ Fallback: Si NO empieza con /public/, normalizar y construir URL
  let normalizedPath = normalizedUrl.replace(/^\/+/, "");
  normalizedPath = normalizedPath.replace(/^public\//, "");
  return `${origin}/public/${normalizedPath}`;
};

// ✅ Función para formatear nombre y número en formato "Nombre - +Número"
const formatSenderDisplay = (senderContact, contact, participant) => {
  // Prioridad 1: senderContact (del backend)
  const name = senderContact?.name || contact?.name || null;
  let number = senderContact?.number || contact?.number || 
               (participant ? participant.replace(/@lid|@s\.whatsapp\.net/g, '') : null);
  
  // Limpiar número (solo dígitos) para comparación
  const cleanNumber = number ? number.replace(/\D/g, '') : null;
  const cleanName = name ? name.replace(/\D/g, '') : null;
  
  // Si tenemos nombre Y número, mostrar "Nombre | +Número"
  if (name && number && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    // Formatear número con prefijo + (agregar solo si no lo tiene)
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return `${name} | ${formattedNumber}`;
  }
  
  // Si solo tenemos nombre (y no es un número), mostrar solo nombre
  if (name && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    return name;
  }
  
  // Si solo tenemos número, mostrar "+Número"
  if (number) {
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return formattedNumber;
  }
  
  // Fallback
  return 'Desconocido';
};

// ✅ Función para generar color único por remitente (similar a WhatsApp Web)
const getSenderColor = (identifier) => {
  // Paleta de colores de WhatsApp Web para remitentes en grupos
  const colors = [
    '#00a884', // Verde WhatsApp
    '#0da9e0', // Azul claro
    '#9c27b0', // Púrpura
    '#e91e63', // Rosa
    '#ff9800', // Naranja
    '#795548', // Marrón
    '#607d8b', // Gris azulado
    '#f44336', // Rojo
    '#4caf50', // Verde claro
    '#2196f3', // Azul
    '#ff5722', // Naranja oscuro
    '#009688', // Verde azulado
    '#673ab7', // Púrpura oscuro
  ];
  
  // Generar hash simple del identificador (número o ID)
  let hash = 0;
  const str = String(identifier || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Usar el hash para seleccionar un color de la paleta
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
    },
  },

  messagesList: {
    backgroundColor: theme.palette.messageBackground || CHAT.paneBg(theme),
    backgroundImage: CHAT.panePattern(theme),
    backgroundRepeat: "repeat",
    backgroundSize: "340px 340px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "16px 16px 28px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      padding: "8px 6px 16px",
    },
  },

  circleLoading: {
    color: theme.palette.primary.main,
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: "18%",
    marginTop: 2,
    minWidth: 100,
    maxWidth: 560,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageLeftBg || CHAT.incomingBg(theme),
    color: theme.palette.messageLeftText || CHAT.incomingText(theme),
    alignSelf: "flex-start",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 16,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    paddingLeft: 9,
    paddingRight: 9,
    paddingTop: 6,
    paddingBottom: 8,
    border: "none",
    fontSize: 14.2,
    lineHeight: 1.4,
    boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
    "& $timestamp": {
      color: CHAT.timestampIn(theme),
    },
    "& $messageActionsButton": {
      color: theme.mode === "dark" ? "#f9fafb" : "#54656f",
      backgroundColor: theme.mode === "dark" ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.92)",
    },
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
    },
    [theme.breakpoints.down("sm")]: {
      marginRight: "6%",
      maxWidth: "92%",
    },
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.quotedLeftBg || CHAT.quotedIn(theme),
    borderRadius: 8,
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.quotedSideLeft || (theme.mode === 'dark' ? "#60a5fa" : "#53bdeb"),
  },

  messageRight: {
    marginLeft: "18%",
    marginTop: 2,
    minWidth: 100,
    maxWidth: 560,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageRightBg || CHAT.outgoingBg(theme),
    color: theme.palette.messageRightText || CHAT.outgoingText(theme),
    alignSelf: "flex-end",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    paddingLeft: 9,
    paddingRight: 9,
    paddingTop: 6,
    paddingBottom: 8,
    fontSize: 14.2,
    lineHeight: 1.4,
    boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
    "& $timestamp": {
      color: CHAT.timestampOut(theme),
    },
    "& $messageActionsButton": {
      color: theme.mode === "dark" ? "#ecfdf5" : "#0f766e",
      backgroundColor: theme.mode === "dark" ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.72)",
    },
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "6%",
      maxWidth: "92%",
    },
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.quotedRightBg || CHAT.quotedOut(theme),
    borderRadius: 8,
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.quotedSideRight || (theme.mode === 'dark' ? "#2dd4bf" : "#06cf9c"),
  },

  messageActionsButton: {
    display: "block",
    position: "absolute",
    zIndex: 10,
    top: 4,
    right: 4,
    padding: 4,
    fontSize: 14,
    borderRadius: "50%",
    width: 24,
    height: 24,
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 140ms ease, visibility 140ms ease, background-color 140ms ease",
    "@media (hover: none)": {
      opacity: 0.85,
      visibility: "visible",
    },
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "rgba(15, 23, 42, 0.18)",
    },
  },

  messageContactName: {
    display: "block",
    fontSize: "12.8px",
    fontWeight: 500,
    marginBottom: "4px",
    lineHeight: "22px",
    // El color se aplicará dinámicamente por remitente
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  telegramButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 8,
    marginBottom: 4,
    marginRight: -72,
    width: "calc(100% + 72px)",
  },
  telegramButtonRow: {
    display: "flex",
    gap: 6,
    width: "100%",
  },
  telegramButton: {
    border: "none",
    borderRadius: 8,
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    color: "#fff",
    flex: 1,
    minWidth: 0,
    textAlign: "center",
    lineHeight: 1.25,
    "&:disabled": {
      opacity: 0.65,
      cursor: "default",
    },
  },
  
  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.messageDeletedText || (theme.mode === 'dark' ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.36)"), // ✅ USA TEMA
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: theme.palette.forwardMessageColor || (theme.mode === 'dark' ? "#9ca3af" : "#999"), // ✅ USA TEMA
    display: "flex",
    alignItems: "center"
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderRadius: 12,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 2,
    right: 7,
    color: CHAT.timestampIn(theme),
    display: "flex",
    alignItems: "center",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.01em",
  },

  dailyTimestamp: {
    display: "inline-block",
    width: "fit-content",
    maxWidth: "100%",
    textAlign: "center",
    alignSelf: "center",
    backgroundColor: theme.palette.dailyTimestampBg || (theme.mode === "dark" ? "#1f2937" : "#ffffff"),
    margin: "14px auto",
    borderRadius: 999,
    border: BORDERS.base(theme),
    boxShadow: SHADOWS.base(theme),
  },

  dailyTimestampText: {
    color: theme.palette.dailyTimestampText || (theme.mode === 'dark' ? "#9ca3af" : "#808888"), // ✅ USA TEMA
    padding: "4px 14px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    fontVariantNumeric: "tabular-nums",
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  ticketNunberClosed: {
    display: "inline-block",
    margin: "12px auto 4px",
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.01em",
    backgroundColor: theme.mode === "dark" ? "rgba(239,68,68,0.16)" : "#fee2e2",
    color: theme.mode === "dark" ? "#fca5a5" : "#b91c1c",
  },

  ticketNunberOpen: {
    display: "inline-block",
    margin: "4px auto 12px",
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.01em",
    backgroundColor: theme.mode === "dark" ? "rgba(16,185,129,0.16)" : "#d1fae5",
    color: theme.mode === "dark" ? "#6ee7b7" : "#047857",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    // Merge inmutable: los ya presentes se reemplazan en su posición, los nuevos
    // (página anterior) se anteponen. Nunca se muta `state`.
    let next = state;

    messages.forEach((message) => {
      const messageIndex = next.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        if (next === state) next = [...state];
        next[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...next];
  }

  if (action.type === "MERGE_LATEST") {
    // Página 1 (los más recientes): los que faltan se agregan al final, en orden.
    let next = state;
    action.payload.forEach((message) => {
      const idx = next.findIndex((m) => m.id === message.id);
      if (next === state) next = [...state];
      if (idx !== -1) next[idx] = message;
      else next.push(message);
    });
    return next;
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      const next = [...state];
      next[messageIndex] = newMessage;
      return next;
    }
    return [...state, newMessage];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex === -1) return state;

    const next = [...state];
    next[messageIndex] = messageToUpdate;
    return next;
  }

  if (action.type === "RESET") {
    return [];
  }
};

/**
 * Normaliza `createdAt` venido por socket/API (Date, ISO con Z/offset, o naive MySQL).
 * Debe pasar por `parseNaiveDate` del hook: convierte instantes UTC a la TZ de empresa.
 *
 * Importante: NO convertir `Date` con `toISOString().substring(...)` a string “naive”:
 * eso elimina la Z y los componentes quedan en UTC pero se interpretan como hora local
 * de la empresa (desfase ~ offset; muy visible en VPS si el payload trae `Date`/ISO).
 */
const normalizeSocketMessageCreatedAt = (raw, parseNaiveDate) => {
  if (raw == null || raw === "") return null;
  const parsed = parseNaiveDate(raw);
  if (parsed && parsed.isValid()) {
    return parsed.format("YYYY-MM-DD HH:mm:ss");
  }
  if (typeof raw === "string") return raw;
  return null;
};

const parseTelegramReplyMarkup = (message) => {
  try {
    const raw = message?.dataJson;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const markup = parsed?.replyMarkup;
    if (!markup || !Array.isArray(markup.rows) || markup.rows.length === 0) return null;
    return markup;
  } catch (e) {
    return null;
  }
};

const telegramButtonColors = ["#229ED9", "#31b545", "#c44c4c", "#64748b"];

const MessagesList = ({ ticket, ticketId, isGroup, readOnlyPreview = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { formatTime, formatDateTime, parseNaiveDate } = useTimezone(); // ✅ Usar hook de timezone

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [telegramClickKey, setTelegramClickKey] = useState("");
  const lastMessageRef = useRef();
  // #7: ref al contenedor en vez de document.getElementById("messagesList"),
  // que colisiona si se monta otro MessagesList (p.ej. TicketMessagesDialog).
  const messagesListRef = useRef(null);
  // #8: se pone en true tras el primer scroll-al-fondo del hilo; evita arrastrar
  // al usuario al recibir mensajes mientras lee historial.
  const didInitialScrollRef = useRef(false);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const isMounted = useRef(true);
  const socketManager = useContext(SocketContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  // ✅ CORRECCIÓN: Extraer ticketId del ticket para evitar expresión compleja en dependencias
  const ticketIdFromTicket = ticket?.id;
  
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    didInitialScrollRef.current = false; // #8: re-armar el auto-scroll para el hilo nuevo

    // ✅ CORRECCIÓN: Actualizar ref con ticketId o ticket.id (priorizar ticketId prop)
    const effectiveTicketId = ticketId || ticketIdFromTicket;
    currentTicketId.current = effectiveTicketId;
    
    // console.log("[MessagesList] TicketId actualizado:", {
    //   ticketId,
    //   ticketIdFromTicket,
    //   effectiveTicketId,
    //   currentTicketIdRef: currentTicketId.current
    // });
    
    // ✅ REMOVIDO: El scroll se hará después de cargar los mensajes
  }, [ticketId, ticketIdFromTicket]);

  useEffect(() => {
    // 0297 Fase A: sin demora artificial previa a GET /messages (antes 500 ms sumaba ~1 s con Ticket).
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) {
          setLoading(false);
          return;
        }
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (Number(currentTicketId.current) === Number(ticketId)) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          } else {
            setLoading(false);
          }

          // ✅ MEJORADO: Scroll al final solo cuando se carga la primera página
          if (pageNumber === 1) {
            // Post-render scroll (0297 A.3): valor bajo; no afecta al fetch inicial
            setTimeout(() => {
              scrollToBottom(true); // carga inicial: siempre al fondo
            }, 120);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 0);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  // ✅ AGREGADO: Scroll al final SOLO la primera vez que el hilo se puebla (página 1).
  // Los mensajes entrantes posteriores los maneja el listener de socket, que
  // respeta si el usuario está leyendo historial (#8).
  useEffect(() => {
    if (messagesList.length > 0 && pageNumber === 1 && !didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      setTimeout(() => {
        scrollToBottom(true);
      }, 50);
    }
  }, [messagesList.length, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // El socket es COMPARTIDO. Registrar con handler nombrado y en el cleanup
    // remover SOLO estos handlers — nunca `socket.off("evento")` a secas
    // (bug #1 de la revisión: borraba listeners de Ticket / otros componentes).
    const listeners = [];
    const on = (event, handler) => {
      socket.on(event, handler);
      listeners.push([event, handler]);
    };

    // ✅ CORRECCIÓN: Función para suscribirse al canal del ticket
    const subscribeToTicket = () => {
      // ✅ Usar ticketId prop O ticket.id como fallback
      const effectiveTicketId = ticketId || ticket?.id || currentTicketId.current;
      if (effectiveTicketId) {
        // console.log("[MessagesList] Suscribiéndose al ticket:", effectiveTicketId);
        socket.emit("joinChatBox", `${effectiveTicketId}`);
      }
    };

    // ✅ CORRECCIÓN: Suscribirse inmediatamente si el ticketId está disponible
      subscribeToTicket();

    // ✅ FIX 05/12/2025: Guardar referencias a los listeners para poder limpiarlos correctamente
    const handleReady = () => {
      subscribeToTicket();
    };
    
    const handleConnect = () => {
      // Re-suscribirse después de una reconexión
      subscribeToTicket();
    };

    // ✅ CORRECCIÓN: Suscribirse cuando el socket está ready
    on("ready", handleReady);

    // ✅ CORRECCIÓN: Re-suscribirse cuando el socket se reconecta
    on("connect", handleConnect);

    // Handler ÚNICO de ingesta de mensajes del ticket abierto.
    // Antes esta misma lógica (~40 líneas) estaba duplicada entre el listener
    // `company-*-appMessage` y el listener del canal por ticket; se registra el
    // mismo handler para ambos eventos. El reducer deduplica por id, así que el
    // doble disparo no crea mensajes repetidos.
    const handleIncomingMessage = (data) => {
      if (!isMounted.current) return;

      if (!data.message || !data.message.id) {
        console.warn("[MessagesList] Mensaje recibido sin ID, ignorando:", data);
        return;
      }

      // ticketId del mensaje desde múltiples fuentes (timing del socket).
      const messageTicketId = data.message?.ticketId || data.ticket?.id;
      const currentTicketIdValue = currentTicketId.current || ticket?.id || ticketId;
      const idsMatch = messageTicketId && currentTicketIdValue &&
        (Number(messageTicketId) === Number(currentTicketIdValue) ||
          String(messageTicketId) === String(currentTicketIdValue));
      if (!idsMatch) return;

      if (data.action !== "create" && data.action !== "update") return;

      const createdAtValue =
        normalizeSocketMessageCreatedAt(data.message.createdAt, parseNaiveDate) ??
        data.message.createdAt ??
        null;

      const normalized = {
        ...data.message,
        ticketId: data.message.ticketId || messageTicketId,
        mediaType: data.message.mediaType || "conversation",
        channel: data.message.channel || "whatsapp",
        fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
        createdAt: createdAtValue,
      };

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: normalized });
        return;
      }

      // create
      dispatch({ type: "ADD_MESSAGE", payload: normalized });
      // #8: si el mensaje es propio, bajar siempre; si es entrante, solo si el
      // usuario ya estaba al final del hilo.
      setTimeout(() => scrollToBottom(normalized.fromMe === true), 100);
    };

    on(`company-${companyId}-appMessage`, handleIncomingMessage);

    on(`company-${companyId}-appMessage-update`, (data) => {
      if (!isMounted.current) {
        return;
      }
      
      // ✅ CORRECCIÓN: Verificar ticketId desde múltiples fuentes
      const currentTicketIdValue = currentTicketId.current || ticket?.id || ticketId;
      const idsMatchUpdate = data.message?.ticketId && currentTicketIdValue && 
        (Number(data.message.ticketId) === Number(currentTicketIdValue) || String(data.message.ticketId) === String(currentTicketIdValue));
      if (idsMatchUpdate) {
        const createdAtValue =
          data.message?.createdAt != null
            ? normalizeSocketMessageCreatedAt(data.message.createdAt, parseNaiveDate) ??
              data.message.createdAt
            : data.message.createdAt;

        dispatch({
          type: "UPDATE_MESSAGE",
          payload: {
            ...data.message,
            createdAt: createdAtValue,
          },
        });
      }
    });

    // Canal por ticket (sala `joinChatBox`). Reutiliza el mismo handler de ingesta.
    const effectiveTicketIdForChannel = ticketId || ticket?.id;
    if (effectiveTicketIdForChannel) {
      on(`${effectiveTicketIdForChannel}`, handleIncomingMessage);
    }

    return () => {
      // Remover SOLO nuestros handlers (con su referencia). El
      // `socket.off("company-*-contact")` y `socket.off("disconnect")` que había
      // aquí borraban listeners de otros componentes para eventos que este
      // efecto ni siquiera registra (bug #1 de la revisión) — eliminados.
      if (socket && typeof socket.off === 'function') {
        listeners.forEach(([event, handler]) => socket.off(event, handler));
      }
    };
    // ✅ FIX 05/12/2025: Cambiar dependencia de 'ticket' (objeto) a 'ticketIdFromTicket' (primitivo)
    // para evitar re-ejecuciones innecesarias cuando el ticket se actualiza (nuevo mensaje, cambio de estado, etc.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticketIdFromTicket, socketManager]);

  // En móvil el socket se corta al bloquear la pantalla o cambiar de app: se
  // re-sincronizan los últimos mensajes al volver, al recuperar red, al
  // reconectar y tras enviar (si el eco del socket no llegó).
  useEffect(() => {
    if (readOnlyPreview) return undefined;
    let inFlight = false;
    const timers = [];

    const syncLatest = async (forceScroll = false) => {
      const id = currentTicketId.current;
      if (!id || inFlight) return;
      inFlight = true;
      try {
        const { data } = await api.get("/messages/" + id, { params: { pageNumber: 1 } });
        if (isMounted.current && Number(currentTicketId.current) === Number(id) && Array.isArray(data?.messages)) {
          dispatch({ type: "MERGE_LATEST", payload: data.messages });
          setTimeout(() => scrollToBottom(forceScroll), 100);
        }
      } catch (err) {
        // silencioso: es una re-sincronización de respaldo
      } finally {
        inFlight = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") syncLatest();
    };
    const onOnline = () => syncLatest();
    const onSent = (e) => {
      if (String(e?.detail?.ticketId) !== String(currentTicketId.current)) return;
      timers.push(setTimeout(() => syncLatest(true), 1500));
    };
    const startedAt = Date.now();
    const onReconnect = () => {
      if (Date.now() - startedAt < 3000) return;
      syncLatest();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onVisible);
    window.addEventListener("watoolx:message-sent", onSent);
    const unsubscribeConnect =
      typeof socketManager?.subscribeConnect === "function"
        ? socketManager.subscribeConnect(onReconnect)
        : () => {};

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("watoolx:message-sent", onSent);
      unsubscribeConnect();
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticketIdFromTicket, socketManager, readOnlyPreview]);

  // ✅ FIX 05/12/2025: UN SOLO useEffect controla isMounted cuando el componente se monta/desmonta
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Sin dependencias = solo al montar/desmontar el componente

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  // #8: ¿el usuario está mirando el final del hilo? Si está leyendo historial
  // (desplazado hacia arriba) no debemos arrastrarlo al fondo.
  const isNearBottom = (threshold = 150) => {
    const el = messagesListRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  // `force` = true para carga inicial y mensajes propios (fromMe); el resto solo
  // baja si el usuario ya estaba al final.
  const scrollToBottom = (force = false) => {
    const messagesContainer = messagesListRef.current;
    if (!messagesContainer) return;
    if (!force && !isNearBottom()) return;

    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest"
      });
    } else {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth"
      });
    }

    // Fallback: asegurar el fondo si el scroll suave no llegó.
    setTimeout(() => {
      if (!messagesListRef.current) return;
      const c = messagesListRef.current;
      if (c.scrollTop < c.scrollHeight - c.clientHeight - 50) {
        c.scrollTop = c.scrollHeight;
      }
    }, 200);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0 && messagesListRef.current) {
      messagesListRef.current.scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const hanldeReplyMessage = (e, message) => {
    if (readOnlyPreview) return;
    //if (ticket.status === "open" || ticket.status === "group") {
    setAnchorEl(null);
    setReplyingMessage(message);
    //}
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    if (readOnlyPreview) return;
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const handleTelegramButtonClick = async (message, button, key) => {
    if (readOnlyPreview || !ticketId) return;
    try {
      setTelegramClickKey(key);
      const { data } = await api.post(`/messages/${ticketId}/telegram-click`, {
        messageId: message.id,
        button,
      });
      if (data?.openUrl) {
        window.open(data.openUrl, "_blank", "noopener,noreferrer");
      }
      if (data?.alert) {
        toast.info(data.alert);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTelegramClickKey("");
    }
  };

  const renderTelegramButtons = (message) => {
    const markup = parseTelegramReplyMarkup(message);
    if (!markup) return null;
    let flatIndex = 0;
    return (
      <div className={classes.telegramButtons}>
        {markup.rows.map((row, rowIndex) => (
          <div key={`${message.id}-row-${rowIndex}`} className={classes.telegramButtonRow}>
            {(Array.isArray(row) ? row : []).map((button, colIndex) => {
              const color = telegramButtonColors[flatIndex % telegramButtonColors.length];
              const key = `${message.id}-${rowIndex}-${colIndex}`;
              flatIndex += 1;
              return (
                <button
                  key={key}
                  type="button"
                  className={classes.telegramButton}
                  style={{ backgroundColor: color }}
                  disabled={readOnlyPreview || Boolean(telegramClickKey)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTelegramButtonClick(message, button, key);
                  }}
                >
                  {button.text}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else
    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      //console.log(array);
      //console.log(contact);
      //console.log(obj[0].number);
      return <VCardPreview contact={contact} numbers={obj[0].number} />
    }
    /* else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    } */
    /*else if (message.mediaType === "multi_vcard") {
      // console.log("multi_vcard")
      // console.log(message)
      
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map((v, idx) => (
              <VcardPreview key={`vcard-${message.id}-${idx}`} contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} message={message} />;
    } else if (message.mediaType === "audio") {
      // ✅ CORRECCIÓN: Normalizar URL de audio (misma solución que imágenes)
      const normalizedAudioUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <audio controls>
          <source src={normalizedAudioUrl} type="audio/mp3"></source>
        </audio>
      );
    } else if (message.mediaType === "video") {
      // ✅ CORRECCIÓN: Normalizar URL de video (misma solución que imágenes)
      const normalizedVideoUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <video
          className={classes.messageMedia}
          src={normalizedVideoUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', // ✅ USA TEMA
              borderRadius: '8px',
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`, // ✅ USA TEMA
              marginBottom: '8px',
              maxWidth: '300px'
            }}>
              {/* Icono del archivo */}
              {(() => {
                // ✅ USAR NOMBRE ORIGINAL DEL ARCHIVO PARA EL ICONO
                let fileName = message.body || 'document';
                
                // Si el body contiene el nombre del archivo, usarlo
                if (message.body && message.body.includes('.')) {
                  fileName = message.body;
                } else if (message.mediaUrl) {
                  // Extraer nombre desde la URL si no hay body
                  const urlFileName = message.mediaUrl.split('/').pop();
                  if (urlFileName && urlFileName.includes('.')) {
                    const parts = urlFileName.split('.');
                    if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                      // Si la primera parte es un número (timestamp), usar el resto
                      fileName = parts.slice(1).join('.');
                    } else {
                      fileName = urlFileName;
                    }
                  }
                }
                
                const extension = fileName.split('.').pop()?.toLowerCase();
                
                // Colores e iconos según el tipo de archivo
                let iconConfig;
                if (['doc', 'docx'].includes(extension)) {
                  iconConfig = { bg: '#2b579a', text: 'W' }; // Word - Azul
                } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
                  iconConfig = { bg: '#217346', text: '📊' }; // Excel/CSV - Verde
                } else if (['pdf'].includes(extension)) {
                  iconConfig = { bg: '#dc3545', text: 'PDF' }; // PDF - Rojo
                } else if (['ppt', 'pptx'].includes(extension)) {
                  iconConfig = { bg: '#d24726', text: 'P' }; // PowerPoint - Naranja
                } else if (['txt'].includes(extension)) {
                  iconConfig = { bg: '#6c757d', text: 'T' }; // Texto - Gris
                } else {
                  iconConfig = { bg: '#6c757d', text: '📄' }; // Default - Gris
                }
                
                return (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: iconConfig.bg
                  }}>
                    {iconConfig.text}
                  </div>
                );
              })()}
              
              {/* Información del archivo */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.mode === 'dark' ? '#f9fafb' : '#212529', // ✅ USA TEMA
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {(() => {
                    // ✅ USAR NOMBRE ORIGINAL DEL ARCHIVO
                    let fileName = message.body || 'Documento';
                    
                    // Si el body contiene el nombre del archivo, usarlo
                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      // Extraer nombre desde la URL si no hay body
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          // Si la primera parte es un número (timestamp), usar el resto
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }
                    
                    return fileName;
                  })()}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d', // ✅ USA TEMA
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(() => {
                    // ✅ USAR TAMAÑO REAL DEL ARCHIVO
                    let fileName = message.body || 'document';
                    
                    // Si el body contiene el nombre del archivo, usarlo
                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      // Extraer nombre desde la URL si no hay body
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          // Si la primera parte es un número (timestamp), usar el resto
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }
                    
                    const extension = fileName.split('.').pop()?.toUpperCase() || 'DOC';
                    const fileSize = message.mediaSize || 0;
                    
                    return `${extension} • ${formatFileSize(fileSize)}`;
                  })()}
                </div>
              </div>
              
              {/* Botón de descarga minimalista */}
              <Button
                size="small"
                style={{
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', // ✅ USA TEMA
                  border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#dee2e6'}`, // ✅ USA TEMA
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d', // ✅ USA TEMA
                  padding: 0
                }}
                target="_blank"
                href={normalizeMediaUrl(message.mediaUrl)}
              >
                <GetApp style={{ fontSize: '16px' }} />
              </Button>
            </div>
          </div>
        </>
      );
    }
};

  /*
    const renderMessageAck = (message) => {
      if (message.ack === 1) {
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 2) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 3) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 4 || message.ack === 5) {
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      }
    };
    */

    const renderMessageAck = (message) => {
      // ✅ NO MOSTRAR ICONO DE RELOJ PARA MENSAJES DEL CONTACTO
      if (!message.fromMe) {
        return null;
      }
      
      if (message.ack === 0) {
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 1) {
        return <CheckCircleOutline fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 2) {
        return <CheckCircle fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 3) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 4) {
        return <DoneAll fontSize="small" className={classes.ackIcons} color="primary" />;
      }
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    };

  const renderDailyTimestamps = (message, index) => {
    // ✅ CORRECCIÓN TIMEZONE: Usar parseNaiveDate del hook useTimezone para parsear correctamente
    if (index === 0) {
      const parsedDate = message.createdAt ? parseNaiveDate(message.createdAt) : null;
      const isValidDate = parsedDate && parsedDate.isValid();
      
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {isValidDate 
              ? parsedDate.format("DD/MM/YYYY")
              : "Fecha no disponible"}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      const messageDay = message.createdAt 
        ? parseNaiveDate(message.createdAt) 
        : null;
      const previousMessageDay = messagesList[index - 1].createdAt
        ? parseNaiveDate(messagesList[index - 1].createdAt)
        : null;

      // ✅ VALIDACIÓN: Verificar que ambas fechas sean válidas antes de comparar y formatear
      const isValidMessageDay = messageDay && messageDay.isValid();
      const isValidPreviousDay = previousMessageDay && previousMessageDay.isValid();

      if (isValidMessageDay && isValidPreviousDay && !isSameDay(messageDay.toDate(), previousMessageDay.toDate())) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {messageDay.format("DD/MM/YYYY")}
            </div>
          </span>
        );
      }
    }
    // ✅ REMOVIDO: El lastMessageRef se aplicará directamente al último mensaje
  };

  const renderNumberTicket = (message, index, filteredMessagesArray) => {
    // ✅ CORREGIDO: Solo mostrar si hay mensaje anterior y el ticketId cambió
    if (index > 0 && index < filteredMessagesArray.length) {
      let messageTicket = message.ticketId;
      let previousMessageTicket = filteredMessagesArray[index - 1].ticketId;

      // ✅ SOLO MOSTRAR SI EL TICKETID REALMENTE CAMBIÓ Y NO ES EL PRIMER MENSAJE
      if (messageTicket && previousMessageTicket && messageTicket !== previousMessageTicket) {
        return (
          <center key={`ticket-change-${messageTicket}-${previousMessageTicket}`}>
            <div className={classes.ticketNunberClosed}>
              Conversación cerrada: {filteredMessagesArray[index - 1].createdAt && !isNaN(new Date(filteredMessagesArray[index - 1].createdAt).getTime()) 
                ? formatDateTime(filteredMessagesArray[index - 1].createdAt)
                : "Fecha no disponible"}
            </div>

            <div className={classes.ticketNunberOpen}>
              Conversación iniciada: {message.createdAt && !isNaN(new Date(message.createdAt).getTime()) 
                ? formatDateTime(message.createdAt)
                : "Fecha no disponible"}
            </div>
          </center>
        );
      }
    }
    return null; // ✅ RETORNAR null SI NO HAY CAMBIO DE TICKET
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span 
              className={classes.messageContactName}
              style={{ 
                color: getSenderColor(
                  message.quotedMsg?.senderContact?.number || 
                  message.quotedMsg?.senderContact?.id || 
                  message.quotedMsg?.contact?.number ||
                  message.quotedMsg?.participant
                )
              }}
            >
              {formatSenderDisplay(message.quotedMsg?.senderContact, message.quotedMsg?.contact, message.quotedMsg?.participant)}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <audio controls>
                  <source src={normalizeMediaUrl(message.quotedMsg.mediaUrl)} type="audio/mp3"></source>
                </audio>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <video
                className={classes.messageMedia}
                src={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                controls
              />
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  color="primary"
                  variant="outlined"
                  target="_blank"
                  href={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image" ? (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          ) : (
            message.quotedMsg?.body
          )}

        </div>
      </div>
    );
  };

  // ✅ FUNCIÓN PARA RENDERIZAR REACCIONES SOBRE EL MENSAJE (SOLO LA ÚLTIMA REACCIÓN)
  const renderMessageReactions = (message) => {
    if (!message.reactions || message.reactions.length === 0) {
      return null;
    }

    // ✅ MOSTRAR SOLO LA ÚLTIMA REACCIÓN (COMO WHATSAPP WEB)
    const lastReaction = message.reactions[message.reactions.length - 1];

    return (
      <div style={{
        position: 'absolute',
        bottom: '-30px', // ✅ POSICIÓN MÁS BAJA PARA NO OCULTAR TIMESTAMP
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '4px 8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
        zIndex: 5,
        fontSize: '16px',
        minWidth: '32px',
        justifyContent: 'center'
      }}>
        <span>{lastReaction.type}</span>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      // ✅ FILTRAR MENSAJES DE REACCIÓN Y MENSAJES VACÍOS
      const filteredMessages = messagesList.filter(message => {
        // ✅ EXCLUIR MENSAJES DE REACCIÓN
        if (message.mediaType === "reactionMessage") {
          return false;
        }
        // ✅ PERMITIR MENSAJES INTERNOS, ALERTAS DE SISTEMA Y LLAMADAS (siempre se muestran)
        if (message.mediaType?.startsWith("internal_") || 
            message.mediaType === "system-alert" || 
            message.mediaType === "call_log") {
          return true;
        }
        // ✅ EXCLUIR MENSAJES VACÍOS (sin body y sin mediaUrl)
        if (!message.body && !message.mediaUrl) {
          return false;
        }
        return true;
      });
      
      const viewMessagesList = filteredMessages.map((message, index) => {

        // ✅ NUEVO (31/12/2025): RENDERIZAR MENSAJES DE ALERTA DE SISTEMA (system-alert)
        if (message.mediaType === "system-alert") {
          // Dividir el mensaje en líneas para formatearlo
          const alertLines = message.body?.split('\n').filter(line => line.trim() !== '') || [];
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                margin: '12px 0',
                padding: '12px 0'
              }}>
                {/* ✅ LÍNEA DIVISORIA */}
                <div style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                  marginBottom: '12px'
                }} />
                
                {/* ✅ BANNER DE ALERTA AMARILLO/NARANJA */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fef3c7', // Amarillo/naranja oscuro en dark, claro en light
                  border: `1px solid ${theme.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
                  borderRadius: '8px',
                  padding: '12px 20px',
                  maxWidth: '600px',
                  width: '100%',
                  color: theme.mode === 'dark' ? '#fbbf24' : '#92400e',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {/* ✅ ICONO DE ALERTA */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                    fontWeight: 600
                  }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    {alertLines.length > 0 && (
                      <span>{alertLines[0]}</span>
                    )}
                  </div>
                  
                  {/* ✅ LÍNEAS ADICIONALES DEL MENSAJE */}
                  {alertLines.length > 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      width: '100%',
                      marginTop: '8px'
                    }}>
                      {alertLines.slice(1).map((line, idx) => (
                        <div key={idx} style={{
                          textAlign: 'left',
                          fontWeight: idx === alertLines.length - 2 ? 500 : 400, // Última línea (timestamp) más clara
                          opacity: idx === alertLines.length - 2 ? 0.8 : 1,
                          fontSize: idx === alertLines.length - 2 ? '12px' : '13px'
                        }}>
                          {line.trim()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }

        // ✅ RENDERIZAR MENSAJES INTERNOS (toast, transfer, note, action)
        if (message.mediaType?.startsWith("internal_")) {
          const internalType = message.mediaType.replace("internal_", "");
          let icon, className;
          
          switch (internalType) {
            case "toast":
              icon = <Notifications className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalToast);
              break;
            case "transfer":
              icon = <SwapHoriz className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalTransfer);
              break;
            case "note":
              icon = <Note className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalNote);
              break;
            case "action":
              icon = <Info className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "signature":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "delete":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            default:
              icon = null;
              className = classes.messageInternal;
          }

          // ✅ DETECTAR SI ES MENSAJE DE TRANSFERENCIA O DEVOLUCIÓN PARA FORMATO ESPECIAL
          const isTransferMessage = internalType === "action" && (
            message.body?.includes("transfirió") || 
            message.body?.includes("devuelta") || 
            message.body?.includes("asignada") ||
            message.body?.includes("cambió de")
          );
          
          // ✅ DEBUG: Log temporal removido para evitar spam en consola
          // if (isTransferMessage && process.env.NODE_ENV === 'development') {
          //   console.log('🔍 Transfer message detected:', {
          //     body: message.body,
          //     bodyLength: message.body?.length,
          //     hasNewlines: message.body?.includes('\n'),
          //     lines: message.body?.split(/\r?\n/),
          //     mediaType: message.mediaType
          //   });
          // }

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              {isTransferMessage ? (
                // ✅ FORMATO ESPECIAL PARA TRANSFERENCIAS Y DEVOLUCIONES - UNA SOLA LÍNEA DIVISORIA
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  margin: '12px 0',
                  padding: '12px 0'
                }}>
                  {/* ✅ UNA SOLA LÍNEA DIVISORIA */}
                  <div style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                    marginBottom: '12px'
                  }} />
                  
                  {/* ✅ CONTENIDO CENTRADO CON ICONO ENTRE LÍNEAS */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: theme.mode === 'dark' ? '#d1d5db' : '#666',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    padding: '0 20px',
                    position: 'relative'
                  }}>
                    {/* ✅ DIVIDIR EL TEXTO EN LÍNEAS Y COLOCAR ICONO ENTRE ELLAS */}
                    {(() => {
                      // Limpiar el texto y dividir por saltos de línea
                      const cleanBody = message.body?.trim() || '';
                      const lines = cleanBody.split(/\r?\n/).filter(line => line.trim() !== '');
                      
                      // ✅ DEBUG: Log removido para evitar spam en consola
                      // if (process.env.NODE_ENV === 'development') {
                      //   console.log('🔍 Transfer message lines:', lines.length, lines);
                      // }
                      
                      // Si tiene 3 o más líneas, usar formato estructurado
                      if (lines.length >= 3) {
                        return (
                          <>
                            {/* Línea 1: "Se transfirió la conversación de usuarios" */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400
                            }}>
                              {lines[0].trim()}
                            </div>
                            
                            {/* Línea 2: Icono y nombre del usuario en la misma línea */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}>
                              {icon && (
                                <span style={{ 
                                  opacity: 0.8,
                                  color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {icon}
                                </span>
                              )}
                              <span>{lines[1].trim()}</span>
                            </div>
                            
                            {/* Línea 3: Fecha y hora */}
                            <div style={{
                              fontWeight: 400,
                              fontSize: '12px',
                              opacity: 0.8
                            }}>
                              {lines[2].trim()}
                            </div>
                          </>
                        );
                      } else if (lines.length === 2) {
                        // Si tiene 2 líneas, mostrar con icono en medio
                        return (
                          <>
                            <div style={{ marginBottom: '4px' }}>
                              {lines[0].trim()}
                            </div>
                            {icon && (
                              <div style={{ 
                                margin: '6px 0',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div>
                              {lines[1].trim()}
                            </div>
                          </>
                        );
                      } else {
                        // Fallback: mostrar mensaje completo con icono arriba
                        return (
                          <>
                            {icon && (
                              <div style={{ 
                                marginBottom: '8px',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {cleanBody}
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                // ✅ FORMATO NORMAL PARA OTROS MENSAJES INTERNOS
                // ✅ NUEVO: Si es internal_signature o internal_delete, alinearlo a la derecha
                (internalType === "signature" || internalType === "delete") ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginRight: 20,
                    marginTop: 2,
                    marginBottom: 2
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                      fontSize: '13px',
                      padding: '4px 12px',
                      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6',
                      borderRadius: '8px'
                    }}>
                      {icon}
                      <span>{message.body}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {formatTime(message.createdAt || new Date())}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={className}>
                    {icon}
                    <span>{message.body}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>
                      {formatTime(message.createdAt || new Date())}
                    </span>
                  </div>
                )
              )}
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          // ✅ DETECTAR SI ES LLAMADA ACEPTADA O PERDIDA
          const isAcceptedCall = message.body && message.body.toLowerCase().includes("aceptada");
          const iconColor = isAcceptedCall ? "#4caf50" : "#df3333"; // Verde para aceptada, rojo para perdida
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                {isGroup && (
                  <span 
                    className={classes.messageContactName}
                    style={{ 
                      color: getSenderColor(
                        message.senderContact?.number || 
                        message.senderContact?.id || 
                        message.contact?.number ||
                        message.participant
                      )
                    }}
                  >
                    {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
                  </span>
                )}
                <div>
                  {isAcceptedCall ? (
                    // ✅ ICONO PARA LLAMADA ACEPTADA: Teléfono normal (verde) - sin X
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1z"/>
                    </svg>
                  ) : (
                    // ✅ ICONO PARA LLAMADA PERDIDA: Teléfono con X (rojo)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                    </svg>
                  )}
                  <span>{message.body || (message.createdAt && !isNaN(new Date(message.createdAt).getTime()) ? `Llamada de voz/vídeo perdida a las ${formatTime(message.createdAt)}` : "Llamada de voz/vídeo perdida")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div
                className={classes.messageLeft}
                title={message.queueId && message.queue?.name}
                onDoubleClick={readOnlyPreview ? undefined : (e) => hanldeReplyMessage(e, message)}
                style={{ position: 'relative' }}
                ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
              >
                {showSelectMessageCheckbox && !readOnlyPreview && (
                  <SelectMessageCheckbox
                    // showSelectMessageCheckbox={showSelectMessageCheckbox}
                    message={message}
                  // selectedMessagesList={selectedMessagesList}
                  // setSelectedMessagesList={setSelectedMessagesList}
                  />
                )}
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Reenviado
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && !message.fromMe && (
                  <span 
                    className={classes.messageContactName}
                    style={{ 
                      color: getSenderColor(
                        message.senderContact?.number || 
                        message.senderContact?.id || 
                        message.contact?.number ||
                        message.participant
                      )
                    }}
                  >
                    {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
                  </span>
                )}

                {/* aviso de mensagem apagado pelo contato */}
                {message.isDeleted && (
                  <div>
                    <span className={"message-deleted"}
                    >Este mensaje fue eliminado por el contacto &nbsp;
                      <Block
                        color="error"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== "reactionMessage" && (
                    <MarkdownWrapper message={message}>
                      {message.mediaType === "locationMessage" || message.mediaType === "contactMessage" 
                        ? null
                        : message.body}
                    </MarkdownWrapper>
                  )}
                  {renderTelegramButtons(message)}
                                  
                  <span className={classes.timestamp}>
                    {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
                    {renderMessageAck(message)}
                  </span>
                  {/* ✅ BOTÓN DE OPCIONES FUERA DEL TIMESTAMP - PARTE SUPERIOR DERECHA (oculto en vista previa / espiar) */}
                  {!readOnlyPreview && (
                  <IconButton
                    size="small"
                    className={classes.messageActionsButton}
                    disabled={message.isDeleted}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore style={{ fontSize: "14px" }} />
                  </IconButton>
                  )}
                </div>
                {/* ✅ MOSTRAR REACCIONES SOBRE EL MENSAJE */}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div 
                className={classes.messageRight}
                onDoubleClick={readOnlyPreview ? undefined : (e) => hanldeReplyMessage(e, message)}
                style={{ position: 'relative' }}
                ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
              >
              {showSelectMessageCheckbox && !readOnlyPreview && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Reenviado
                    </span>
                    <br />
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {/* Mostrar el body para todos los tipos de mensaje excepto ubicaciones */}
                  {message.mediaType !== "locationMessage" && 
                   message.body && (
                    <MarkdownWrapper message={message}>{message.body}</MarkdownWrapper>
                  )}
                  {renderTelegramButtons(message)}
                  
                
                  <span className={classes.timestamp}>
                    {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
                    {renderMessageAck(message)}
                  </span>
                  {/* ✅ BOTÓN DE OPCIONES (oculto en vista previa / espiar) */}
                  {!readOnlyPreview && (
                  <IconButton
                    size="small"
                    className={classes.messageActionsButton}
                    disabled={message.isDeleted}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore style={{ fontSize: "14px" }} />
                  </IconButton>
                  )}
                </div>
                {/* ✅ MOSTRAR REACCIONES SOBRE EL MENSAJE */}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>{i18n.t("hardcodedElements.sayHelloToNewContact")}</div>;
    }
  };

  // #12: memoizar el resultado de renderMessages(). Antes se filtraba y mapeaba
  // TODO el hilo en CADA render del componente (abrir/cerrar el menú de opciones,
  // toggles de loading/hasMore, paginación en curso, etc.). Ahora solo se recalcula
  // cuando cambian entradas que afectan al render de los mensajes.
  // NOTA: sigue recalculando el hilo entero cuando `messagesList` cambia (un mensaje
  // nuevo re-renderiza toda la lista). Para O(1) por mensaje haría falta extraer un
  // <MessageItem> con React.memo — pendiente, requiere verificación en navegador.
  const renderedMessages = useMemo(
    () => (messagesList.length > 0 ? renderMessages() : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      messagesList,
      classes,
      theme,
      isGroup,
      readOnlyPreview,
      showSelectMessageCheckbox,
      formatTime,
      formatDateTime,
      parseNaiveDate,
      telegramClickKey,
    ]
  );

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id={ticketId ? `messagesList-${ticketId}` : "messagesList"}
        ref={messagesListRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderedMessages}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;